"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { Holding } from "@/lib/portfolio/holdings";
import { api } from "@/trpc/react";

type HoldingsSource = "live" | "empty" | "unknown";

export interface PlaidHoldingsContextValue {
  holdings: Holding[];
  loading: boolean;
  error: string | null;
  totalValue: number;
  hasHoldings: boolean;
  lastFetched: string | null;
  source: HoldingsSource;
  clientId: string | null;
  setClientId: (clientId: string | null) => void;
  refetch: () => Promise<void>;
  clearHoldings: () => void;
}

const PlaidHoldingsContext = createContext<
  PlaidHoldingsContextValue | undefined
>(undefined);

export interface PlaidHoldingsProviderProps {
  children: ReactNode;
  clientId?: string;
}

export function PlaidHoldingsProvider({
  children,
  clientId,
}: PlaidHoldingsProviderProps) {
  const [currentClientId, setCurrentClientId] = useState<string | undefined>(
    clientId,
  );

  const utils = api.useUtils();

  // Only create query input if we have a clientId - prevents queries with undefined
  // Use a dummy input when no clientId to satisfy TypeScript, but keep query disabled
  const queryInput = currentClientId
    ? { clientId: currentClientId }
    : { clientId: "" };

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: trpcRefetch,
  } = api.portfolio.getClientPortfolio.useQuery(queryInput, {
    enabled: false, // Always disabled - only fetch when explicitly requested via refetch()
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false, // Disable retries to prevent automatic attempts
    // Prevent this query from being included in batch requests when disabled
    gcTime: 0, // Don't cache when disabled
  });

  const holdings = useMemo(() => data?.holdingsRaw ?? [], [data?.holdingsRaw]);
  const totalValue = data?.totalValue ?? 0;
  const lastFetched = data?.fetchedAt ?? null;
  const source: HoldingsSource =
    data?.source === "live" || data?.source === "empty"
      ? data.source
      : "unknown";

  const loading = isLoading || isFetching;
  const hasHoldings = holdings.length > 0;
  const errorMessage = error?.message ?? null;

  const handleRefetch = useCallback(async () => {
    await trpcRefetch();
  }, [trpcRefetch]);

  const clearHoldings = useCallback(() => {
    const input = currentClientId ? { clientId: currentClientId } : undefined;
    utils.portfolio.getClientPortfolio.setData(input, (previous) => {
      if (!previous) return previous;
      return { ...previous, holdingsRaw: [], totalValue: 0 };
    });
  }, [utils, currentClientId]);

  const handleSetClientId = useCallback((next: string | null) => {
    setCurrentClientId(next ?? undefined);
  }, []);

  const value = useMemo<PlaidHoldingsContextValue>(
    () => ({
      holdings,
      loading,
      error: errorMessage,
      totalValue,
      hasHoldings,
      lastFetched,
      source,
      clientId: currentClientId ?? null,
      setClientId: handleSetClientId,
      refetch: handleRefetch,
      clearHoldings,
    }),
    [
      holdings,
      loading,
      errorMessage,
      totalValue,
      hasHoldings,
      lastFetched,
      source,
      currentClientId,
      handleSetClientId,
      handleRefetch,
      clearHoldings,
    ],
  );

  return (
    <PlaidHoldingsContext.Provider value={value}>
      {children}
    </PlaidHoldingsContext.Provider>
  );
}

export function usePlaidHoldingsContext(): PlaidHoldingsContextValue {
  const context = useContext(PlaidHoldingsContext);
  if (!context) {
    throw new Error(
      "usePlaidHoldingsContext must be used within a PlaidHoldingsProvider",
    );
  }
  return context;
}
